import { FC, useMemo, useState } from 'react';

import { useReportData } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { ReportComponent } from '@/types';
import { GlobeConfig } from '@/types/api/modules/business-intelligence';

import GlobalTradeMap from './GlobalTradeMap';
import {
    filterTradeFlows,
    getVisibleTradeHubs,
    globalTradeRowsToMapData,
    isGlobalTradeViewDefinition,
    TradeFlow,
    TradeHub,
} from './model';

interface GlobalTradeGlobeReportComponentProps {
    component: ReportComponent;
}

const GlobalTradeGlobeReportComponent: FC<GlobalTradeGlobeReportComponentProps> = ({ component }) => {
    const configuration = component.configuration as GlobeConfig | undefined;
    const view = configuration?.view;
    const [selectedFlowId, setSelectedFlowId] = useState<string>();
    const [selectedHubId, setSelectedHubId] = useState<string>();
    const validView = isGlobalTradeViewDefinition(view) ? view : undefined;
    const { data } = useReportData(component);
    const mapData = useMemo(() => globalTradeRowsToMapData(data.rows), [data.rows]);
    const flows = useMemo(
        () => (validView ? filterTradeFlows(mapData.flows, mapData.hubs, validView.filters) : []),
        [mapData.flows, mapData.hubs, validView],
    );
    const hubs = useMemo(() => getVisibleTradeHubs(flows, mapData.hubs), [flows, mapData.hubs]);
    const selectedFlow = flows.find((flow: TradeFlow) => flow.id === selectedFlowId);
    const selectedHub = hubs.find((hub: TradeHub) => hub.id === selectedHubId);

    if (!validView) {
        return null;
    }

    return (
        <div style={{ height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden', width: '100%' }}>
            <GlobalTradeMap
                fitToContainer
                flows={flows}
                hubs={hubs}
                mapView={{
                    ...validView.viewport,
                    city: 'Saved Global Trade View',
                    timeZone: 'UTC',
                }}
                mapTheme={validView.presentation.theme}
                onClearSelection={() => {
                    setSelectedFlowId(undefined);
                    setSelectedHubId(undefined);
                }}
                onSelectFlow={(flowId) => {
                    setSelectedFlowId(flowId);
                    setSelectedHubId(undefined);
                }}
                onSelectHub={(hubId) => {
                    setSelectedHubId(hubId);
                    setSelectedFlowId(undefined);
                }}
                projection={validView.presentation.projection}
                resetRequest={0}
                selectedFlow={selectedFlow}
                selectedHub={selectedHub}
                showFlows={validView.layers.corridors}
                showHubs={validView.layers.hubs}
            />
        </div>
    );
};

export default GlobalTradeGlobeReportComponent;
